import {
	type LoaderFunctionArgs,
	type MetaFunction,
	json,
} from "@remix-run/cloudflare";
import { flow, groupBy, sortBy } from "lodash-es";
import {
	type TravelListingLoaderProps,
	getTravelListingComponent,
} from "../components/TravelListing";
import {
	type TravelPost,
	getPosts,
	stripPostsForListing,
} from "../data/travelPosts";
import type { Page, SitemapDataFunction } from "../lib/sitemapUtils";

export default getTravelListingComponent({
	getPostProps(post) {
		return {
			title: post.town,
			to: post.pathname,
		};
	},
});

export const meta: MetaFunction<typeof loader> = ({ data }) => {
	if (!data) throw new Error("No data");

	const { title } = mapCountryToSitemap(data.posts);

	return [{ title }];
};

export async function loader({ params }: LoaderFunctionArgs) {
	const { country } = params;

	if (typeof country !== "string") {
		throw new Error("Expected a single `countrySlug`");
	}

	const slugs = { countrySlug: country, townSlug: null };
	const posts = flow(
		() => getPosts(slugs),
		(_) => sortBy(_, (post) => post.town),
		(_) => stripPostsForListing(_),
	)();

	if (!posts.length) {
		throw new Response("Not Found", {
			status: 404,
		});
	}

	return json<TravelListingLoaderProps>({ ...slugs, posts });
}

export const sitemap: SitemapDataFunction = async () => {
	const posts = stripPostsForListing(getPosts());
	const countries = Object.values(groupBy(posts, (post) => post.countrySlug));
	const sortedCountries = sortBy(countries, (country) => country[0]?.country);

	return sortedCountries.map(mapCountryToSitemap);
};

// TODO: Share sitemap / meta data?
function mapCountryToSitemap(country: TravelPost[]): Page {
	const [post] = country;

	if (!post) {
		throw new Error("No posts found for country");
	}

	return {
		pathname: `/travel/${post.countrySlug}`,
		title: post.country,
		sitemapTitle: `${post.countryEmoji} ${post.country}`,
		description: `Photos from ${post.country}.`,
		images: country.map((post) => post.mainImage),
		order: 1,
	};
}
